import { useEffect, useState } from "react";
import { Activity, RefreshCw, UserPlus, CreditCard, Repeat, Pencil, Trash2, Tag, Wallet } from "lucide-react";
import api from "../../lib/api";
import { isoToEU } from "../../lib/format";

/**
 * Activity Feed — reads the last N audit_log entries via
 * `GET /api/admin/audit-logs` and renders them as a compact vertical
 * timeline. Each row shows the classified action (manual_create,
 * status_change, payment_confirm), the actor's email, the booking
 * subject (customer + vehicle) and a human-readable timestamp.
 */
const ACTION_META = {
  manual_create:      { Icon: UserPlus,   color: "text-emerald-600 dark:text-emerald-400", label: "New reservation",     verb: "created" },
  status_change:      { Icon: Repeat,     color: "text-sky-600 dark:text-sky-400",         label: "Status change",       verb: "updated status of" },
  payment_confirm:    { Icon: CreditCard, color: "text-violet-600 dark:text-violet-400",   label: "Payment confirmed",   verb: "confirmed payment for" },
  payment_change:     { Icon: CreditCard, color: "text-amber-600 dark:text-amber-400",     label: "Payment change",      verb: "changed payment for" },
  balance_collected:  { Icon: Wallet,     color: "text-emerald-700 dark:text-emerald-300", label: "Balance collected",   verb: "collected balance for" },
  update:             { Icon: Pencil,     color: "text-neutral-500 dark:text-neutral-400", label: "Booking update",      verb: "updated" },
  delete:             { Icon: Trash2,     color: "text-rose-600 dark:text-rose-400",       label: "Booking deleted",     verb: "deleted" },
  voucher_delete:     { Icon: Tag,        color: "text-rose-600 dark:text-rose-400",       label: "Voucher deleted",     verb: "deleted voucher" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60)   return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  return days < 7 ? `${days}d ago` : isoToEU(iso.slice(0, 10));
}

export default function ActivityFeedPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api.get("/admin/audit-logs?limit=10")
      .then((r) => setLogs(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load activity"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);            // auto-refresh every 60s
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-6 rounded"
      data-testid="activity-feed-panel"
    >
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          <Activity size={20} className="text-neutral-500 dark:text-neutral-400" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              Operations
            </p>
            <h2 className="font-display text-2xl mt-1">Activity feed</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="activity-feed-refresh"
          className="text-[11px] tracking-widest uppercase border-b border-black/30 dark:border-white/30 hover:border-black dark:hover:border-white pb-0.5 flex items-center gap-1 disabled:opacity-40"
          aria-label="Refresh activity"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-rose-400" data-testid="activity-feed-error">{error}</p>
      )}
      {loading && logs.length === 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400" data-testid="activity-feed-loading">
          Loading recent activity…
        </p>
      )}
      {!loading && logs.length === 0 && !error && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400" data-testid="activity-feed-empty">
          No activity recorded yet.
        </p>
      )}

      {logs.length > 0 && (
        <ol className="space-y-3">
          {logs.map((l, i) => {
            const meta = ACTION_META[l.action] || ACTION_META.update;
            const Icon = meta.Icon;
            const subject = l.action === "voucher_delete"
              ? (l.voucher_code || "voucher")
              : (l.customer_name || l.customer_email || (l.booking_id ? l.booking_id.slice(0, 8) : "booking"));
            const vehicle = l.vehicle_label ? ` · ${l.vehicle_label}` : "";
            const detail = (l.action === "status_change" && l.to_status)
              ? ` → ${l.to_status}`
              : (l.action === "payment_confirm" || l.action === "payment_change" || l.action === "balance_collected") && l.to_payment_status
                ? ` → ${l.to_payment_status}`
                : "";
            return (
              <li
                key={l.id || i}
                className="flex gap-3 py-2 border-b border-black/5 dark:border-white/5 last:border-b-0"
                data-testid={`activity-item-${i}`}
              >
                <div className={`shrink-0 mt-0.5 ${meta.color}`}>
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-neutral-900 dark:text-neutral-100 leading-snug">
                    <span className="font-medium">{l.actor_email || "system"}</span>
                    <span className="text-neutral-500 dark:text-neutral-400"> {meta.verb} </span>
                    <span className="font-medium">{subject}</span>
                    <span className="text-neutral-500 dark:text-neutral-400">{vehicle}{detail}</span>
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                    <span
                      className={`px-1.5 py-0.5 rounded ${meta.color} bg-current/10`}
                      data-testid={`activity-action-${i}`}
                    >
                      {meta.label}
                    </span>
                    <span title={l.created_at}>{relativeTime(l.created_at)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
